/** Domain row types — snake_case mirrors the Postgres schema so rows flow
 *  between Supabase, Dexie and the UI without mapping layers. */

export type AnimalCategory = 'bird' | 'raptor' | 'mammal' | 'reptile' | 'rabbit' | 'other';
export type AnimalStatus = 'active' | 'released' | 'deceased' | 'archived';
export type AnimalCondition = 'stable' | 'watch' | 'critical';

export interface Animal {
  id: string;
  name: string;
  species: string | null;
  kind: 'individual' | 'batch';
  count: number;
  category: AnimalCategory;
  status: AnimalStatus;
  condition: AnimalCondition;
  life_stage: string | null;
  intake_date: string | null;
  released_date: string | null;
  diet: string | null;
  diet_notes: string | null;
  notes: string | null;
  cover_photo_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Schedule {
  id: string;
  animal_id: string;
  label: string | null;
  stype: 'interval' | 'times';
  interval_minutes: number | null;
  anchor_at: string | null;
  times: string[] | null; // 'HH:MM:SS'
  days_of_week: number[];
  fasting_dates: string[];
  feed_every_n_days: number | null;
  active_start: string | null;
  active_end: string | null;
  amount: number | null;
  unit: string | null;
  food: string | null;
  prep_notes: string | null;
  enabled: boolean;
  snoozed_until: string | null;
  last_fed_at: string | null;
  next_due_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedLog {
  id: string;
  animal_id: string;
  schedule_id: string | null;
  fed_at: string;
  amount: number | null;
  unit: string | null;
  food: string | null;
  notes: string | null;
  weight_g: number | null;
  fed_by: string | null;
  skipped: boolean;
  source: 'manual' | 'voice' | 'agent' | 'notification' | 'import';
  created_at: string;
}

export interface Timer {
  id: string;
  label: string;
  animal_id: string | null;
  due_at: string;
  notified_at: string | null;
  completed_at: string | null;
  canceled_at: string | null;
  created_at: string;
}

export interface Photo {
  id: string;
  animal_id: string | null;
  storage_path: string;
  kind: 'animal' | 'document';
  caption: string | null;
  taken_at: string;
  width: number | null;
  height: number | null;
  created_at: string;
}

export interface DocumentRow {
  id: string;
  photo_id: string | null;
  status: 'uploaded' | 'processing' | 'extracted' | 'confirmed' | 'failed';
  raw_text: string | null;
  extraction: unknown;
  error: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Report {
  id: string;
  rtype: 'handoff' | 'daily' | 'weekly' | 'per_animal' | 'audit';
  title: string;
  content_md: string;
  params: Record<string, unknown>;
  created_at: string;
}

export interface Profile {
  onerow: boolean;
  display_name: string | null;
  lat: number | null;
  lng: number | null;
  theme_mode: 'auto' | 'light' | 'dark';
  tts_voice: string | null;
  keepers: string[];
  settings: Record<string, unknown>;
  ics_token: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageRow {
  id: string;
  day: string;
  role: 'user' | 'assistant' | 'system';
  content: unknown;
  created_at: string;
}

/** Initials for avatars: "Willow & Reed" -> "WR", "Acorn" -> "AC". */
export function initials(name: string): string {
  const words = name.split(/[\s&+·]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function avatarClass(category: AnimalCategory): string {
  switch (category) {
    case 'bird': return 'avatar--bird';
    case 'raptor': return 'avatar--raptor';
    case 'mammal': return 'avatar--mammal';
    case 'reptile': return 'avatar--reptile';
    case 'rabbit': return 'avatar--rabbit';
    default: return '';
  }
}
